import React, { useState, useEffect } from 'react';
import { useUser } from '../../contexts/UserContext';
import './styles/Account.css';

export default function Account() {
  const [isEditing, setIsEditing] = useState(false);
  const [activeTab, setActiveTab] = useState('profile');
  const { currentUser, updateProfile, updateVisibilitySetting } = useUser();

  // Mock posts data with privacy settings
  const [posts, setPosts] = useState([
    {
      id: 1,
      content: 'Just arrived in Jamaica! The beaches here are absolutely stunning. #Caribbean #Travel',
      images: ['https://i.natgeofe.com/k/43e66349-5085-4e3c-b6f5-d7268c603044/jamaica-river-raft.jpg?wp=1&w=1084.125&h=609'],
      likes: 24,
      comments: 5,
      date: '2025-06-20T14:30:00',
      privacy: 'public',
      location: 'Montego Bay, Jamaica'
    },
    {
      id: 2,
      content: 'Found this amazing LGBTQ+ friendly resort in St. Lucia. The staff has been so welcoming! #QueerTravel #SafeSpaces',
      images: ['https://images.unsplash.com/photo-1566073771259-6a8506099945?w=800&h=600'],
      likes: 42,
      comments: 8,
      date: '2025-06-15T09:15:00',
      privacy: 'friends',
      location: 'Castries, St. Lucia'
    },
    {
      id: 3,
      content: 'Personal note about my experience navigating Trinidad as a queer traveler. DM me if you need tips!',
      images: [],
      likes: 12,
      comments: 3,
      date: '2025-06-10T16:45:00',
      privacy: 'private',
      location: 'Port of Spain, Trinidad'
    },
    {
      id: 4,
      content: 'Met some amazing people at the Pride event in Barbados yesterday! So grateful for this community.',
      images: ['https://www.sandals.com/blog/content/images/2023/08/Caribbean-Carnival-Festival-Costumes.jpg'],
      likes: 56,
      comments: 14,
      date: '2025-06-05T20:00:00',
      privacy: 'public',
      location: 'Bridgetown, Barbados'
    }
  ]);

  const [userInfo, setUserInfo] = useState({
    name: 'John Doe',
    pronouns: 'he/him',
    identityTags: ['Non-binary', 'queer'],
    orientation: 'Gay',
    bio: 'Software developer passionate about creating amazing user experiences. Love travelling and photography.',
    travelStyle: 'Solo',
    favoriteDestinations: ['Amsterdam', 'Berlin', 'Toronto'],
    additionalTags: ['solo travel', 'nature', 'pride events', 'safe housing'],
    country: 'United States',
    mobileNumber: '',
    profilePicture: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=200&h=200&fit=crop&crop=face',
    coverPhoto:   'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=1200&h=400&fit=crop',
    privacySettings: {
      name: 'public',
      pronouns: 'public',
      identityTags: 'public',
      orientation: 'public',
      bio: 'public',
      travelStyle: 'public',
      favoriteDestinations: 'public',
      additionalTags: 'public',
      country: 'public',
      mobileNumber: 'private'
    }
  });

  /* ----------------------------------------------------------------- */
  /* helpers                                                           */
  /* ----------------------------------------------------------------- */
  const handleInputChange = (field, value) => {
    setUserInfo(prev => ({ ...prev, [field]: value }));
  };

  const handleCommaListChange = (field, value) => {
    handleInputChange(field, value.split(',').map(t => t.trim()).filter(Boolean));
  };

  const handleSave = () => {
    setIsEditing(false);
    // In a real app, this would update the user profile in the backend
    updateProfile(userInfo);
    console.log('Saved user info:', userInfo);
  };
  const handleCancel = () => setIsEditing(false);

  /* ----------------------------------------------------------------- */
  /* Privacy settings                                                  */
  /* ----------------------------------------------------------------- */
  const handlePrivacyChange = (field, value) => {
    setUserInfo(prev => ({
      ...prev,
      privacySettings: {
        ...prev.privacySettings,
        [field]: value
      }
    }));
  };

  /* ----------------------------------------------------------------- */
  /* Select / option data                                              */
  /* ----------------------------------------------------------------- */
  const orientationOptions = [
    'Straight', 'Gay', 'Lesbian', 'Bisexual', 'Pansexual', 'Asexual', 'Prefer not to say'
  ];
  const travelStyles = ['Solo', 'Couple', 'Group', 'Family', 'Backpacking', 'Luxury'];
  const countries = [
    'United States', 'Canada', 'United Kingdom', 'Australia', 'Germany',
    'France', 'Japan', 'Brazil', 'India', 'Mexico', 'Other'
  ];
  
  const privacyOptions = ['public', 'friends', 'private'];
  
  /* ----------------------------------------------------------------- */
  /* Post management                                                   */
  /* ----------------------------------------------------------------- */
  const handlePostPrivacyChange = (postId, newPrivacy) => {
    setPosts(prevPosts => prevPosts.map(post => 
      post.id === postId ? { ...post, privacy: newPrivacy } : post
    ));
  };

  const deletePost = (postId) => {
    setPosts(prevPosts => prevPosts.filter(post => post.id !== postId));
  };

  const addNewPost = (postContent, privacy = 'public', images = [], location = '') => {
    const newPost = {
      id: Date.now(), // Simple way to generate unique IDs
      content: postContent,
      images: images,
      likes: 0,
      comments: 0,
      date: new Date().toISOString(),
      privacy: privacy,
      location: location
    };
    
    setPosts(prevPosts => [newPost, ...prevPosts]);
  };

  /* ----------------------------------------------------------------- */
  /* Post rendering                                                    */
  /* ----------------------------------------------------------------- */
  const renderPosts = () => {
    return (
      <div className="account-posts-container">
        <h2 style={{marginBottom: '12px'}}>My Posts</h2>
        
        {/* New Post Form */}
        <div className="account-new-post">
          <textarea 
            placeholder="What's on your mind?" 
            className="account-post-input"
            id="newPostContent"
          />
          <div className="account-post-actions">
            <select 
              className="account-post-privacy" 
              defaultValue="public"
              id="newPostPrivacy"
            >
              <option value="public">Public</option>
              <option value="friends">Friends Only</option>
              <option value="private">Private</option>
            </select>
            <button 
              className="account-post-submit" 
              onClick={() => {
                const content = document.getElementById('newPostContent').value;
                const privacy = document.getElementById('newPostPrivacy').value;
                if (content.trim()) {
                  addNewPost(content, privacy);
                  document.getElementById('newPostContent').value = '';
                }
              }}
            >
              Post
            </button>
          </div>
        </div>
        
        {/* Posts List */}
        {posts.map(post => (
          <div key={post.id} className={`account-post account-post-${post.privacy}`}>
            <div className="account-post-header">
              <div className="account-post-user-info">
                <img src={userInfo.profilePicture} alt="Profile" className="account-post-avatar" />
                <div>
                  <div className="account-post-name">{userInfo.name}</div>
                  <div className="account-post-meta">
                    <span className="account-post-date">{new Date(post.date).toLocaleDateString()}</span>
                    <span className="account-post-privacy-indicator">
                      {post.privacy === 'public' ? '🌎' : post.privacy === 'friends' ? '👥' : '🔒'}
                      {post.privacy}
                    </span>
                    {post.location && <span className="account-post-location">📍 {post.location}</span>}
                  </div>
                </div>
              </div>
              <div className="account-post-actions">
                <select
                  value={post.privacy}
                  onChange={(e) => handlePostPrivacyChange(post.id, e.target.value)}
                  className="account-post-privacy-select"
                >
                  {privacyOptions.map(option => (
                    <option key={option} value={option}>{option}</option>
                  ))}
                </select>
                <button 
                  className="account-post-delete" 
                  onClick={() => deletePost(post.id)}
                >
                  Delete
                </button>
              </div>
            </div>
            
            <div className="account-post-content">{post.content}</div>
            
            {post.images && post.images.length > 0 && (
              <div className="account-post-images">
                {post.images.map((img, index) => (
                  <img key={index} src={img} alt="Post content" className="account-post-image" />
                ))}
              </div>
            )}
            
            <div className="account-post-stats">
              <span className="account-post-likes">❤️ {post.likes}</span>
              <span className="account-post-comments">💬 {post.comments}</span>
            </div>
          </div>
        ))}
      </div>
    );
  };

  /* ----------------------------------------------------------------- */
  /* render                                                            */
  /* ----------------------------------------------------------------- */
  return (
    <div className="account-container">
      {/* Tab Navigation */}
      <div className="account-tabs">
        <button 
          className={`account-tab ${activeTab === 'profile' ? 'active' : ''}`}
          onClick={() => setActiveTab('profile')}
        >
          Profile
        </button>
        <button 
          className={`account-tab ${activeTab === 'posts' ? 'active' : ''}`}
          onClick={() => setActiveTab('posts')}
        >
          My Posts
        </button>
      </div>

      {activeTab === 'profile' && (
        <>
          {/* ---------------- Cover photo & avatar ---------------- */}
          <div className="account-cover-section">
        <div className="account-cover-photo">
          <img src={userInfo.coverPhoto} alt="Cover" className="account-cover-image" />
          <button className="account-cover-edit-btn">
            {/* icon omitted for brevity */}
            Edit Cover Photo
          </button>
        </div>

        <div className="account-profile-picture">
          <img src={userInfo.profilePicture} alt="Profile" className="account-profile-image" />
          <button className="account-profile-edit-btn">{/* icon */}</button>
        </div>
      </div>

      {/* ---------------- main card ---------------- */}
      <div className="account-main-content">
        <div className="account-header">
          <div className="account-user-info">
            <h1 className="account-user-name">{userInfo.name}</h1>
            {userInfo.pronouns && (
              <span className="account-pronouns">{userInfo.pronouns}</span>
            )}
            {/* Quick access to posts */}
            <div className="account-quick-links">
              <button 
                className="account-posts-link" 
                onClick={() => setActiveTab('posts')}
              >
                View My Posts ({posts.length})
              </button>
            </div>
            {/* identity / community tags */}
            {!isEditing && (
              <div className="account-tags-container" style={{marginLeft: '-4px'}}>
                {userInfo.identityTags.map(tag => (
                  <span key={tag} className="account-tag">{tag}</span>
                ))}
              </div>
            )}
          </div>

          <button
            className="account-edit-btn"
            onClick={() => setIsEditing(!isEditing)}
          >
            {isEditing ? 'Cancel' : 'Edit Profile'}
          </button>
        </div>

        {/* ---------------- details section ---------------- */}
        <div className="account-details-section">
          <div className="account-details-card">
            <h2 className="account-section-title">About</h2>

            {/* -------- VIEW MODE -------- */}
            {!isEditing && (
              <>
                <p className="account-detail-item"><strong>Bio:</strong> {userInfo.bio}</p>

                <p className="account-detail-item">
                  <strong>Orientation:</strong> {userInfo.orientation}
                </p>

                <p className="account-detail-item">
                  <strong>Travel style:</strong> {userInfo.travelStyle}
                </p>

                <p className="account-detail-item">
                  <strong>Favourite destinations:</strong>
                  <span className="account-tags-container" style={{marginTop: '8px'}}>
                    {userInfo.favoriteDestinations.map(tag => (
                      <span key={tag} className="account-tag">{tag}</span>
                    ))}
                  </span>
                </p>

                <p className="account-detail-item">
                  <strong>Additional tags:</strong>
                  <span className="account-tags-container" style={{marginTop: '8px'}}>
                    {userInfo.additionalTags.map(tag => (
                      <span key={tag} className="account-tag">{tag}</span>
                    ))}
                  </span>
                </p>

                <p className="account-detail-item"><strong>Country:</strong> {userInfo.country}</p>

                {userInfo.mobileNumber && userInfo.privacySettings.mobileNumber !== 'private' && (
                  <p className="account-detail-item"><strong>Mobile Number:</strong> {userInfo.mobileNumber}</p>
                )}

                <div className="account-privacy-info">
                  <h3>Privacy Settings</h3>
                  <p className="account-privacy-note">Fields marked as private will only be visible to you.</p>
                </div>
              </>
            )}

            {/* -------- EDIT MODE -------- */}
            {isEditing && (
              <div className="account-details-edit">
                <label className="account-label">Name
                  <input
                    className="account-input"
                    value={userInfo.name}
                    onChange={e => handleInputChange('name', e.target.value)}
                  />
                </label>

                <label className="account-label">Pronouns
                  <input
                    className="account-input"
                    placeholder="e.g. she/her"
                    value={userInfo.pronouns}
                    onChange={e => handleInputChange('pronouns', e.target.value)}
                  />
                </label>

                <label className="account-label">Identity tags (comma-separated)
                  <input
                    className="account-input"
                    value={userInfo.identityTags.join(', ')}
                    onChange={e => handleCommaListChange('identityTags', e.target.value)}
                  />
                </label>

                <label className="account-label">Orientation
                  <select
                    className="account-select"
                    value={userInfo.orientation}
                    onChange={e => handleInputChange('orientation', e.target.value)}
                  >
                    {orientationOptions.map(o => <option key={o}>{o}</option>)}
                  </select>
                </label>

                <label className="account-label">Travel style
                  <select
                    className="account-select"
                    value={userInfo.travelStyle}
                    onChange={e => handleInputChange('travelStyle', e.target.value)}
                  >
                    {travelStyles.map(s => <option key={s}>{s}</option>)}
                  </select>
                </label>

                <label className="account-label">Favourite destinations
                  <input
                    className="account-input"
                    placeholder="Comma-separated list"
                    value={userInfo.favoriteDestinations.join(', ')}
                    onChange={e => handleCommaListChange('favoriteDestinations', e.target.value)}
                  />
                </label>

                <label className="account-label">Additional tags
                  <input
                    className="account-input"
                    placeholder="Comma-separated list"
                    value={userInfo.additionalTags.join(', ')}
                    onChange={e => handleCommaListChange('additionalTags', e.target.value)}
                  />
                </label>

                <label className="account-label">Country
                  <select
                    className="account-select"
                    value={userInfo.country}
                    onChange={e => handleInputChange('country', e.target.value)}
                  >
                    {countries.map(c => <option key={c}>{c}</option>)}
                  </select>
                </label>

                <label className="account-label">Mobile Number
                  <input
                    className="account-input"
                    placeholder="e.g. +1 (555) 123-4567"
                    value={userInfo.mobileNumber}
                    onChange={e => handleInputChange('mobileNumber', e.target.value)}
                  />
                </label>

                <h3 className="account-privacy-heading">Privacy Settings</h3>
                <p className="account-privacy-description">Control who can see your information</p>
                
                <div className="account-privacy-settings">
                  <label className="account-label">Name Privacy
                    <select
                      className="account-select"
                      value={userInfo.privacySettings.name}
                      onChange={e => handlePrivacyChange('name', e.target.value)}
                    >
                      {privacyOptions.map(option => <option key={option}>{option}</option>)}
                    </select>
                  </label>

                  <label className="account-label">Pronouns Privacy
                    <select
                      className="account-select"
                      value={userInfo.privacySettings.pronouns}
                      onChange={e => handlePrivacyChange('pronouns', e.target.value)}
                    >
                      {privacyOptions.map(option => <option key={option}>{option}</option>)}
                    </select>
                  </label>

                  <label className="account-label">Identity Tags Privacy
                    <select
                      className="account-select"
                      value={userInfo.privacySettings.identityTags}
                      onChange={e => handlePrivacyChange('identityTags', e.target.value)}
                    >
                      {privacyOptions.map(option => <option key={option}>{option}</option>)}
                    </select>
                  </label>

                  <label className="account-label">Orientation Privacy
                    <select
                      className="account-select"
                      value={userInfo.privacySettings.orientation}
                      onChange={e => handlePrivacyChange('orientation', e.target.value)}
                    >
                      {privacyOptions.map(option => <option key={option}>{option}</option>)}
                    </select>
                  </label>

                  <label className="account-label">Bio Privacy
                    <select
                      className="account-select"
                      value={userInfo.privacySettings.bio}
                      onChange={e => handlePrivacyChange('bio', e.target.value)}
                    >
                      {privacyOptions.map(option => <option key={option}>{option}</option>)}
                    </select>
                  </label>

                  <label className="account-label">Travel Style Privacy
                    <select
                      className="account-select"
                      value={userInfo.privacySettings.travelStyle}
                      onChange={e => handlePrivacyChange('travelStyle', e.target.value)}
                    >
                      {privacyOptions.map(option => <option key={option}>{option}</option>)}
                    </select>
                  </label>

                  <label className="account-label">Favorite Destinations Privacy
                    <select
                      className="account-select"
                      value={userInfo.privacySettings.favoriteDestinations}
                      onChange={e => handlePrivacyChange('favoriteDestinations', e.target.value)}
                    >
                      {privacyOptions.map(option => <option key={option}>{option}</option>)}
                    </select>
                  </label>

                  <label className="account-label">Additional Tags Privacy
                    <select
                      className="account-select"
                      value={userInfo.privacySettings.additionalTags}
                      onChange={e => handlePrivacyChange('additionalTags', e.target.value)}
                    >
                      {privacyOptions.map(option => <option key={option}>{option}</option>)}
                    </select>
                  </label>

                  <label className="account-label">Country Privacy
                    <select
                      className="account-select"
                      value={userInfo.privacySettings.country}
                      onChange={e => handlePrivacyChange('country', e.target.value)}
                    >
                      {privacyOptions.map(option => <option key={option}>{option}</option>)}
                    </select>
                  </label>

                  <label className="account-label">Mobile Number Privacy
                    <select
                      className="account-select"
                      value={userInfo.privacySettings.mobileNumber}
                      onChange={e => handlePrivacyChange('mobileNumber', e.target.value)}
                    >
                      {privacyOptions.map(option => <option key={option}>{option}</option>)}
                    </select>
                  </label>
                </div>

                <div className="account-form-actions">
                  <button className="account-save-btn" onClick={handleSave}>Save Changes</button>
                  <button className="account-cancel-btn" onClick={handleCancel}>Cancel</button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
        </>
      )}
      
      {activeTab === 'posts' && renderPosts()}
      
      {/* Floating Action Button for quick access to posts */}
      {activeTab === 'profile' && (
        <button 
          className="account-floating-btn" 
          onClick={() => setActiveTab('posts')}
          title="View My Posts"
        >
          <span className="account-floating-btn-icon">📝</span>
          <span className="account-floating-btn-badge">{posts.length}</span>
        </button>
      )}
    </div>
  );
}
